import {
  generateCUIT as generateCUITShared,
  validateCUIT as validateCUITShared,
  validateDNI as validateDNIShared,
  validateEmail as validateEmailShared,
  validatePhone as validatePhoneShared,
  normalizeString as normalizeStringShared,
} from '../../../utils/validationUtils';

/**
 * Filtra suscriptores por término de búsqueda
 * @param {Array} subscribers - Lista de suscriptores
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array} - Suscriptores filtrados
 */
export const filterSubscribers = (subscribers, searchTerm) => {
  if (!searchTerm.trim()) {
    return subscribers;
  }

  const term = searchTerm.toLowerCase();
  return subscribers.filter(
    (subscriber) =>
      subscriber.first_name?.toLowerCase().includes(term) ||
      subscriber.last_name?.toLowerCase().includes(term) ||
      subscriber.dni?.toLowerCase().includes(term) ||
      subscriber.email?.toLowerCase().includes(term)
  );
};

/**
 * Genera CUIT a partir de DNI y género
 * @param {string} dni - Número de DNI
 * @param {string} genero - Género ('h', 'm', 'O')
 * @returns {string} - CUIT completo
 */
export const generateCUIT = (dni, genero) => generateCUITShared(dni, genero);

/**
 * Valida un DNI
 * @param {string} dni - DNI a validar
 * @returns {boolean} - True si es válido
 */
export const validateDNI = (dni) => validateDNIShared(dni);

/**
 * Valida un CUIT
 * @param {string} cuit - CUIT a validar
 * @returns {boolean} - True si es válido
 */
export const validateCUIT = (cuit) => validateCUITShared(cuit);

/**
 * Valida un email
 * @param {string} email - Email a validar
 * @returns {boolean} - True si es válido
 */
export const validateEmail = (email) => validateEmailShared(email, { optional: true });

/**
 * Valida un teléfono
 * @param {string} phone - Teléfono a validar
 * @returns {boolean} - True si es válido
 */
export const validatePhone = (phone) => validatePhoneShared(phone, { optional: true });

/**
 * Formatea un nombre para mostrar
 * @param {string} firstName - Nombre
 * @param {string} lastName - Apellido
 * @returns {string} - Nombre completo formateado
 */
export const formatFullName = (firstName, lastName) => {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';
  return `${last}, ${first}`.trim().replace(/^,\s*|,\s*$/g, '');
};

/**
 * Normaliza un string removiendo acentos y convirtiendo a minúsculas
 * @param {string} str - String a normalizar
 * @returns {string} - String normalizado
 */
export const normalizeString = (str) => normalizeStringShared(str);

/**
 * Prepara los datos del suscriptor para envío a la API
 * @param {Object} subscriber - Datos del suscriptor
 * @returns {Object} - Datos preparados para la API
 */
export const prepareSubscriberData = (subscriber) => {
  const cleanedData = { ...subscriber };

  // Limpiar campos vacíos
  Object.keys(cleanedData).forEach((key) => {
    if (cleanedData[key] === '' || cleanedData[key] === null) {
      delete cleanedData[key];
    }
  });

  // Formatear fecha de nacimiento
  if (cleanedData.birth_date && cleanedData.birth_date.length > 10) {
    cleanedData.birth_date = cleanedData.birth_date.substring(0, 10);
  }

  return cleanedData;
};

/**
 * Obtiene el nombre de un distrito por UUID
 * @param {Array} districts - Lista de distritos
 * @param {string} uuid - UUID del distrito
 * @returns {string} - Nombre del distrito
 */
export const getDistrictName = (districts, uuid) => {
  const district = districts.find((d) => d.uuid === uuid);
  return district?.name || 'Sin distrito';
};

/**
 * Obtiene la ubicación completa de un suscriptor
 * @param {Object} subscriber - Suscriptor
 * @param {Array} countries - Lista de países
 * @param {Array} provinces - Lista de provincias
 * @param {Array} departments - Lista de departamentos
 * @param {Array} districts - Lista de distritos
 * @returns {Object} - Ubicación completa
 */
export const getSubscriberLocation = (subscriber, countries, provinces, departments, districts) => {
  const district = districts.find((d) => d.uuid === subscriber.uuid_district);
  const department = departments.find((d) => d.uuid === district?.uuid_department);
  const province = provinces.find((p) => p.uuid === department?.uuid_province);
  const country = countries.find((c) => c.uuid === province?.uuid_country);

  return {
    district,
    department,
    province,
    country,
  };
};
